import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { useBot } from "@/context/BotContext";
import Layout from "@/components/Layout";
import { ProxyListCard } from "@/components/ProxyListCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Save, RotateCcw, DollarSign, Target, Zap, Shield, CheckCircle, XCircle, Loader2, Trash2, TrendingDown, Layers, AlertTriangle } from "lucide-react";

const API = "/api";

const Row = ({ label, desc, children }) => (
  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 py-3" style={{ borderBottom: '1px solid rgba(30, 42, 58, 0.3)' }}>
    <div className="flex-1 min-w-0">
      <div className="text-sm" style={{ color: 'var(--text-primary)' }}>{label}</div>
      {desc && <div className="text-[10px] mt-0.5" style={{ color: 'var(--text-secondary)' }}>{desc}</div>}
    </div>
    <div className="sm:w-44">{children}</div>
  </div>
);

const StepResult = ({ step }) => {
  const icon = step.ok
    ? <CheckCircle className="w-3.5 h-3.5 shrink-0" style={{ color: 'var(--accent-teal)' }} />
    : <XCircle className="w-3.5 h-3.5 shrink-0" style={{ color: 'var(--accent-red)' }} />;
  const nameMap = {
    private_key: "Private Key", wallet_address: "Wallet Address", api_creds: "API Credentials",
    builder_creds: "Builder API", market: "BTC Market", proxy: "Proxy",
    init: "Initialize", sign: "Order Signing", post: "Order Posting", creds: "Credentials",
  };
  return (
    <div className="flex items-start gap-2 py-1">
      {icon}
      <div className="min-w-0">
        <span className="text-[11px] font-medium" style={{ color: 'var(--text-primary)' }}>{nameMap[step.name] || step.name}</span>
        <span className="text-[10px] ml-2" style={{ color: 'var(--text-secondary)' }}>{step.detail}</span>
      </div>
    </div>
  );
};

const DEFAULT_FADE = [10, 20];
const FADE_STEPS = [
  { label: "Bet 1 · DOWN", rule: "After a new green candle" },
  { label: "Bet 2 · UP", rule: "If bet 1 loses (2nd green)" },
];

// ============ GREEN-FADE BETS ============
const FadeBetsCard = ({ bets, bankroll, onChange }) => {
  const values = Array.isArray(bets) && bets.length === 2 ? bets : DEFAULT_FADE;
  const total = values.reduce((a, b) => a + (Number(b) || 0), 0);
  const setStep = (i, v) => {
    const arr = [...values];
    arr[i] = v === "" ? "" : parseFloat(v);
    onChange(arr);
  };
  return (
    <div data-testid="fade-bets-card" className="card-base">
      <div className="card-header flex items-center justify-between">
        <span className="flex items-center gap-2 text-xs uppercase tracking-wider text-[var(--text-secondary)] font-semibold">
          <Layers className="w-3.5 h-3.5 text-[var(--accent-teal)]" />Green-Fade Bets
        </span>
        <button type="button" data-testid="fade-bets-reset-btn" onClick={() => onChange([...DEFAULT_FADE])}
          className="text-[10px] px-2 py-1 rounded transition-colors hover:text-[var(--text-primary)]"
          style={{ background: 'var(--bg-tertiary)', color: 'var(--text-secondary)' }}>Reset default</button>
      </div>
      <div className="card-content space-y-2.5">
        {values.map((amt, i) => (
          <div key={i} className="flex items-center gap-2.5">
            <div className="w-6 h-6 flex items-center justify-center text-[10px] font-bold rounded-md shrink-0"
              style={{ background: i === 0 ? 'rgba(255,77,106,0.12)' : 'rgba(0,229,168,0.12)', color: i === 0 ? 'var(--accent-red)' : 'var(--accent-teal)' }}>{i + 1}</div>
            <div className="flex-1 min-w-0">
              <div className="text-xs font-semibold text-[var(--text-primary)]">{FADE_STEPS[i].label}</div>
              <div className="text-[10px]" style={{ color: 'var(--text-secondary)' }}>{FADE_STEPS[i].rule}</div>
            </div>
            <Input data-testid={`fade-bet-${i + 1}-input`} type="number" min="1" step="1" value={amt}
              onChange={(e) => setStep(i, e.target.value)}
              onBlur={(e) => { if (!e.target.value || parseFloat(e.target.value) <= 0) setStep(i, DEFAULT_FADE[i]); }}
              className="input-base w-24 text-right font-mono" />
          </div>
        ))}
        <div className="pt-3 mt-1 space-y-1.5" style={{ borderTop: '1px solid var(--border)' }}>
          <div className="flex justify-between text-xs"><span className="text-[var(--text-secondary)]">Max loss per cycle</span><span data-testid="fade-max-loss" className="font-mono text-[var(--accent-red)]">${total.toFixed(2)}</span></div>
          <div className="flex justify-between text-xs"><span className="text-[var(--text-secondary)]">% of Bankroll</span><span className="font-mono text-[var(--text-primary)]">{bankroll ? ((total / bankroll) * 100).toFixed(1) : "0.0"}%</span></div>
        </div>
        <div data-testid="fade-rules" className="mt-2 text-[10px] leading-relaxed rounded-lg p-2.5"
          style={{ background: 'rgba(0,229,168,0.05)', border: '1px solid rgba(0,229,168,0.18)', color: 'var(--text-secondary)' }}>
          <div><b style={{ color: 'var(--text-primary)' }}>Green candle</b> → bet 1 DOWN on the next market</div>
          <div><b style={{ color: 'var(--accent-teal)' }}>Red next</b> → win bet 1, cycle ends</div>
          <div><b style={{ color: 'var(--accent-yellow)' }}>Green again</b> → bet 2 UP (double) on the next market</div>
          <div><b style={{ color: 'var(--accent-red)' }}>Green, green, red</b> → both bets lost</div>
          <div className="mt-1 opacity-80">After each cycle it waits for a NEW green candle (a still-running green streak doesn't count). Results use Polymarket's official settlement.</div>
        </div>
      </div>
    </div>
  );
};

// ============ SETTINGS PAGE ============
const Settings = () => {
  const { token } = useAuth();
  const { selectedBotId, mode, selectedBot, updateBot, fetchBots, bots } = useBot();
  const [s, setS] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [changed, setChanged] = useState(false);

  const [credKey, setCredKey] = useState("");
  const [credAddress, setCredAddress] = useState("");
  const [credWalletType, setCredWalletType] = useState("email");
  const [credChanged, setCredChanged] = useState(false);
  const [savingCreds, setSavingCreds] = useState(false);

  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [testingLive, setTestingLive] = useState(false);
  const [liveResult, setLiveResult] = useState(null);
  const [resettingPnl, setResettingPnl] = useState(false);

  const headers = token ? { Authorization: `Bearer ${token}` } : {};
  const botParam = selectedBotId ? `?bot_id=${selectedBotId}` : "";

  const load = useCallback(async () => {
    if (!selectedBotId) return;
    try {
      const r = await axios.get(`${API}/settings${botParam}`, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
      setS(r.data);
      setChanged(false);
      setCredAddress(r.data.polymarket_funder_address || "");
      setCredWalletType(r.data.polymarket_wallet_type || "email");
      setCredKey("");
      setCredChanged(false);
    } catch { toast.error("Failed to load settings"); }
    finally { setLoading(false); }
  }, [token, selectedBotId]);

  useEffect(() => { load(); }, [load]);

  const set = (k, v) => { setS(p => ({ ...p, [k]: v })); setChanged(true); };

  const save = async (overrides = null) => {
    const data = overrides || s;
    setSaving(true);
    try {
      await axios.put(`${API}/settings${botParam}`, {
        entry_min_percent: data.entry_min_percent, entry_max_percent: data.entry_max_percent,
        daily_profit_target: data.daily_profit_target || null,
        max_bet_size: data.max_bet_size || null, bankroll: data.bankroll,
        fade_bets: (Array.isArray(data.fade_bets) && data.fade_bets.length === 2 ? data.fade_bets : DEFAULT_FADE).map((v, i) => (Number(v) > 0 ? Number(v) : DEFAULT_FADE[i])),
        stop_loss_enabled: !!data.stop_loss_enabled,
        stop_loss_percent: typeof data.stop_loss_percent === 'number' ? data.stop_loss_percent : 10,
        stop_loss_time_left_minutes: typeof data.stop_loss_time_left_minutes === 'number' ? data.stop_loss_time_left_minutes : 1,
      }, { headers });
      toast.success("Settings saved"); setChanged(false);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to save — check connection"); }
    finally { setSaving(false); }
  };

  const saveCredentials = async () => {
    setSavingCreds(true);
    try {
      const payload = { polymarket_wallet_type: credWalletType };
      // Only send funder address if user actually entered one — never send null/empty to avoid wiping existing address
      if (credAddress && credAddress.trim()) {
        payload.polymarket_funder_address = credAddress.trim();
      }
      if (credKey.trim()) payload.polymarket_private_key = credKey.trim();
      // Save to bot-specific credentials endpoint
      const endpoint = selectedBotId ? `${API}/bots/${selectedBotId}/save-credentials` : `${API}/settings/save-polymarket-credentials`;
      const r = await axios.post(endpoint, payload, { headers });
      if (r.data.success) {
        toast.success(r.data.message || "Credentials saved");
        setCredKey(""); setCredChanged(false); load(); fetchBots();
      } else toast.error(r.data.error || "Failed to save");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to save credentials"); }
    finally { setSavingCreds(false); }
  };

  const cloneCredentialsFrom = async (sourceBotId) => {
    if (!selectedBotId || !sourceBotId) return;
    const sourceBot = bots.find(b => b.id === sourceBotId);
    if (!sourceBot) return;
    if (!window.confirm(`Copy private key & wallet address from "${sourceBot.name}" to this bot? Any existing credentials on this bot will be replaced.`)) return;
    setSavingCreds(true);
    try {
      const r = await axios.post(`${API}/bots/${selectedBotId}/clone-credentials/${sourceBotId}`, {}, { headers });
      if (r.data.success) {
        toast.success(r.data.message || "Credentials cloned");
        setCredKey(""); setCredChanged(false); load(); fetchBots();
      } else toast.error(r.data.error || "Failed to clone");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to clone credentials"); }
    finally { setSavingCreds(false); }
  };

  const reset = async () => {
    if (!window.confirm("Reset all settings to defaults?")) return;
    try { await axios.post(`${API}/settings/reset`, {}, { headers }); toast.success("Reset"); load(); }
    catch { toast.error("Failed"); }
  };

  const clearKey = async () => {
    if (!window.confirm("Remove your private key from this bot?")) return;
    try {
      if (selectedBotId) {
        await axios.post(`${API}/bots/${selectedBotId}/save-credentials`, { polymarket_private_key: "" }, { headers });
      } else {
        await axios.put(`${API}/settings`, { polymarket_private_key: "" }, { headers });
      }
      toast.success("Key removed"); load(); fetchBots();
    }
    catch { toast.error("Failed to clear key"); }
  };

  const resetPnl = async () => {
    if (!window.confirm("Reset all PnL stats and delete trade history for this bot?")) return;
    setResettingPnl(true);
    try {
      const params = selectedBotId ? `?bot_id=${selectedBotId}` : "";
      await axios.post(`${API}/settings/reset-pnl${params}`, {}, { headers });
      toast.success("PnL and trade history reset");
    }
    catch { toast.error("Failed to reset PnL"); }
    finally { setResettingPnl(false); }
  };

  const testConnection = async () => {
    setTesting(true); setTestResult(null);
    try {
      const params = selectedBotId ? `?bot_id=${selectedBotId}` : "";
      const r = await axios.post(`${API}/settings/test-connection${params}`, {}, { headers });
      setTestResult(r.data);
      if (r.data.success) toast.success("All connection checks passed!");
      else toast.error(r.data.error || "Some checks failed");
    } catch (e) {
      toast.error("Test request failed");
      setTestResult({ success: false, error: e.response?.data?.detail || "Request failed", steps: [] });
    } finally { setTesting(false); }
  };

  const testLiveOrder = async () => {
    if (!window.confirm("This will place a real $2.50 test order on Polymarket. Continue?")) return;
    setTestingLive(true); setLiveResult(null);
    try {
      const params = selectedBotId ? `?bot_id=${selectedBotId}` : "";
      const r = await axios.post(`${API}/settings/test-live-order${params}`, {}, { headers, timeout: 180000 });
      setLiveResult(r.data);
      if (r.data.success) toast.success("Order placed! " + (r.data.order_id || ""));
      else toast.error(r.data.error?.slice(0, 100) || "Order failed");
    } catch (e) {
      toast.error("Test order request failed");
      setLiveResult({ success: false, error: e.response?.data?.detail || "Request failed or timed out", steps_log: [] });
    } finally { setTestingLive(false); }
  };

  if (loading) return <Layout><div className="flex items-center justify-center h-[60vh]"><div className="text-[var(--accent-teal)] font-mono text-sm animate-pulse">Loading...</div></div></Layout>;

  const hasCreds = s?.has_polymarket_private_key;

  return (
    <Layout>
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <h1 data-testid="settings-title" className="text-[21px] font-bold tracking-tight" style={{ color: 'var(--text-primary)' }}>Settings</h1>
            <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded" style={{
              background: mode === "live" ? 'rgba(0, 229, 168, 0.1)' : 'rgba(0, 194, 255, 0.1)',
              color: mode === "live" ? 'var(--accent-teal)' : 'var(--accent-cyan)',
              border: `1px solid ${mode === "live" ? 'rgba(0, 229, 168, 0.2)' : 'rgba(0, 194, 255, 0.2)'}`,
            }}>{selectedBot?.name || mode}</span>
          </div>
          <div className="flex gap-2">
            <Button data-testid="reset-settings-btn" onClick={reset} disabled={saving} className="btn-ghost text-[var(--text-secondary)] h-8 px-3 text-xs">
              <RotateCcw className="w-3 h-3 mr-1.5" />Reset
            </Button>
            <Button data-testid="save-settings-btn" onClick={() => save()} disabled={saving || !changed} className="btn-primary h-8 px-4 text-xs" style={{ color: '#0B0F1A' }}>
              <Save className="w-3 h-3 mr-1.5" />{saving ? "..." : "Save Settings"}
            </Button>
          </div>
        </div>

        {changed && <div className="bg-[rgba(0,229,168,0.04)] border border-[rgba(0,229,168,0.2)] rounded-lg px-4 py-2 text-xs text-[var(--accent-teal)] font-medium">Unsaved changes</div>}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2 space-y-4">

            {/* Bot Mode — paper vs live */}
            <div className="card-base">
              <div className="card-header flex items-center gap-2">
                <Shield className="w-3.5 h-3.5 text-[var(--accent-teal)]" />
                <span className="text-xs uppercase tracking-wider text-[var(--text-secondary)] font-semibold">Bot Mode</span>
                <span className="ml-auto text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded"
                  style={{
                    background: mode === "live" ? 'rgba(0,229,168,0.15)' : 'rgba(0,194,255,0.15)',
                    border: `1px solid ${mode === "live" ? 'rgba(0,229,168,0.45)' : 'rgba(0,194,255,0.45)'}`,
                    color: mode === "live" ? '#00E5A8' : '#00C2FF',
                  }}>
                  {mode === "live" ? "● LIVE" : "○ PAPER"}
                </span>
              </div>
              <div className="card-content">
                <p className="text-[11px] mb-2.5" style={{ color: 'var(--text-secondary)' }}>
                  {mode === "live"
                    ? "This bot places REAL orders on Polymarket using your wallet."
                    : "This bot is SIMULATING trades — no real orders are placed on Polymarket."}
                </p>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { value: "paper", label: "○ PAPER", desc: "Simulated, no real money", color: "#00C2FF" },
                    { value: "live", label: "● LIVE", desc: "Real orders on Polymarket", color: "#00E5A8" },
                  ].map(opt => (
                    <button key={opt.value} data-testid={`bot-mode-${opt.value}-btn`}
                      onClick={async () => {
                        if (mode === opt.value) return;
                        const isSwitchingToLive = opt.value === "live";
                        const msg = isSwitchingToLive
                          ? "Switch this bot to LIVE? It will place REAL orders on Polymarket next time it runs."
                          : "Switch this bot to PAPER? Real trading will stop and switch to simulation.";
                        if (!window.confirm(msg)) return;
                        try {
                          await updateBot(selectedBotId, { mode: opt.value });
                        } catch (err) {
                          console.error("Switch mode failed:", err);
                        }
                      }}
                      className="flex flex-col items-start gap-0.5 px-3 py-2.5 rounded-lg transition-all text-left"
                      style={{
                        background: mode === opt.value ? `${opt.color}10` : 'var(--bg-primary)',
                        border: `1px solid ${mode === opt.value ? `${opt.color}40` : 'var(--border)'}`,
                      }}>
                      <span className="text-xs font-bold" style={{ color: mode === opt.value ? opt.color : 'var(--text-primary)' }}>
                        {opt.label}
                      </span>
                      <span className="text-[10px]" style={{ color: 'var(--text-secondary)' }}>{opt.desc}</span>
                    </button>
                  ))}
                </div>
                {mode === "paper" && (
                  <p className="text-[10px] mt-2.5 px-2.5 py-1.5 rounded"
                    style={{ background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.25)', color: 'var(--accent-yellow)' }}>
                    ⚠ While this bot is in paper mode, you'll see simulated wins/losses on the dashboard but nothing on Polymarket.
                  </p>
                )}
              </div>
            </div>

            {/* Strategy */}
            <div className="card-base">
              <div className="card-header flex items-center gap-2"><DollarSign className="w-3.5 h-3.5 text-[var(--accent-teal)]" /><span className="text-xs uppercase tracking-wider text-[var(--text-secondary)] font-semibold">Strategy</span></div>
              <div className="card-content">
                <Row label="Market Window" desc={`Current: ${selectedBot?.window_minutes || 5}-min BTC market. Restart the bot after changing.`}>
                  <Select
                    value={String(selectedBot?.window_minutes || 5)}
                    onValueChange={async (v) => {
                      const newWm = parseInt(v, 10);
                      if (!window.confirm(`Switch this bot to ${newWm === 60 ? "1-hour" : newWm === 1440 ? "Daily" : newWm + "-min"} BTC markets? If the bot is running, stop and restart it for the change to take full effect.`)) return;
                      try {
                        await updateBot(selectedBotId, { window_minutes: newWm });
                        toast.success(`Window switched. Restart the bot if it was running.`);
                      } catch (err) {
                        console.error("Window update failed:", err);
                        toast.error("Failed to update window");
                      }
                    }}
                  >
                    <SelectTrigger data-testid="bot-window-select" className="input-base"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="5">5-min BTC</SelectItem>
                      <SelectItem value="15">15-min BTC</SelectItem>
                      <SelectItem value="60">1-hour BTC</SelectItem>
                      <SelectItem value="1440">Daily BTC</SelectItem>
                    </SelectContent>
                  </Select>
                </Row>
                <Row label="Direction" desc="Set automatically by the strategy">
                  <div data-testid="direction-auto" className="input-base h-9 flex items-center px-3 text-xs font-bold rounded-md"
                    style={{ color: 'var(--accent-teal)', border: '1px solid rgba(0,229,168,0.3)', background: 'rgba(0,229,168,0.06)' }}>DOWN, then UP</div>
                </Row>
                <Row label="Bankroll" desc="Total allocation">
                  <Input data-testid="bankroll-input" type="number" min="100" step="100" value={s.bankroll ?? ""} onChange={(e) => set("bankroll", e.target.value === "" ? "" : parseFloat(e.target.value))} onBlur={(e) => { if (!e.target.value) set("bankroll", 1000); }} className="input-base" />
                </Row>
                <Row label="Stop Loss" desc="Auto-sell the position if the price drops below the threshold within the time-left window. Applies to live trades only.">
                  <button
                    type="button"
                    data-testid="stop-loss-toggle"
                    onClick={() => set("stop_loss_enabled", !s.stop_loss_enabled)}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${s.stop_loss_enabled ? "bg-[var(--accent-red)]" : "bg-[var(--bg-tertiary)]"}`}
                  >
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${s.stop_loss_enabled ? "translate-x-6" : "translate-x-1"}`} />
                  </button>
                </Row>
                {s.stop_loss_enabled && (
                  <>
                    <Row label="Stop Loss %" desc="Sell if price ≤ this % (e.g. 10 = sell when contract is at 10¢)">
                      <Input data-testid="stop-loss-percent-input" type="number" min="1" max="99" step="1" value={s.stop_loss_percent ?? ""} onChange={(e) => set("stop_loss_percent", e.target.value === "" ? "" : parseFloat(e.target.value))} onBlur={(e) => { if (!e.target.value) set("stop_loss_percent", 10); }} className="input-base" />
                    </Row>
                    <Row label="Time Left (min)" desc="Only trigger stop-loss when this many minutes (or less) remain in the market">
                      <Input data-testid="stop-loss-time-left-input" type="number" min="0.1" max="60" step="0.1" value={s.stop_loss_time_left_minutes ?? ""} onChange={(e) => set("stop_loss_time_left_minutes", e.target.value === "" ? "" : parseFloat(e.target.value))} onBlur={(e) => { if (!e.target.value) set("stop_loss_time_left_minutes", 1); }} className="input-base" />
                    </Row>
                  </>
                )}
              </div>
            </div>

            {/* Entry + Risk */}
            <div className="card-base">
              <div className="card-header flex items-center gap-2"><Target className="w-3.5 h-3.5 text-[var(--accent-teal)]" /><span className="text-xs uppercase tracking-wider text-[var(--text-secondary)] font-semibold">Entry & Risk</span></div>
              <div className="card-content">
                <Row label="Entry Min %" desc="Minimum contract price">
                  <Input data-testid="entry-min-input" type="number" min="1" max="99" value={s.entry_min_percent ?? ""} onChange={(e) => set("entry_min_percent", e.target.value === "" ? "" : parseFloat(e.target.value))} onBlur={(e) => { if (!e.target.value) set("entry_min_percent", 35); }} className="input-base" />
                </Row>
                <Row label="Entry Max %" desc="Maximum contract price">
                  <Input data-testid="entry-max-input" type="number" min="1" max="99" value={s.entry_max_percent ?? ""} onChange={(e) => set("entry_max_percent", e.target.value === "" ? "" : parseFloat(e.target.value))} onBlur={(e) => { if (!e.target.value) set("entry_max_percent", 65); }} className="input-base" />
                </Row>
                <Row label="Profit Target" desc="Pause at this profit (0=off)">
                  <Input data-testid="daily-profit-input" type="number" min="0" step="10" value={s.daily_profit_target ?? ""} onChange={(e) => set("daily_profit_target", e.target.value === "" ? null : parseFloat(e.target.value))} className="input-base" placeholder="Off" />
                </Row>
                <Row label="Max Bet" desc="Max single bet (0=no limit)">
                  <Input data-testid="max-bet-input" type="number" min="0" step="10" value={s.max_bet_size ?? ""} onChange={(e) => set("max_bet_size", e.target.value === "" ? null : parseFloat(e.target.value))} className="input-base" placeholder="None" />
                </Row>
              </div>
            </div>

            {/* Polymarket Connection — only in live mode */}
            {mode === "live" && (
            <div className="card-base border border-[rgba(0,229,168,0.2)]">
              <div className="card-header flex items-center gap-2">
                <Shield className="w-3.5 h-3.5 text-[var(--accent-teal)]" />
                <span className="text-xs uppercase tracking-wider text-[var(--text-secondary)] font-semibold">Polymarket Connection</span>
                {hasCreds && s?.has_wallet_address && <span className="ml-auto text-[10px] text-[var(--accent-teal)] font-mono flex items-center gap-1"><CheckCircle className="w-3 h-3" />Connected</span>}
                {hasCreds && !s?.has_wallet_address && credWalletType !== "eoa" && <span className="ml-auto text-[10px] text-[var(--accent-yellow)] font-mono flex items-center gap-1"><AlertTriangle className="w-3 h-3" />Wallet Address Missing</span>}
              </div>
              <div className="card-content">
                <div className="bg-[var(--bg-card)] border border-[var(--border)] px-3 py-2 mb-4 text-[10px] text-[var(--text-secondary)] font-mono leading-relaxed">
                  <span className="text-[var(--accent-teal)] font-semibold">Connect your Polymarket account for live trading:</span>
                  <br />1. Go to <span className="text-[var(--text-primary)]">polymarket.com &gt; Settings &gt; Export Private Key</span> &gt; paste below
                  <br />2. Go to <span className="text-[var(--text-primary)]">polymarket.com &gt; Profile icon</span> &gt; copy your wallet address &gt; paste below
                  <br />3. Click <span className="text-[var(--accent-teal)]">Save Credentials</span> &gt; <span className="text-[var(--accent-teal)]">Test Connection</span> &gt; <span className="text-amber-400">Test Order</span>
                </div>

                {/* Warning banner if wallet address is missing */}
                {hasCreds && !credAddress && credWalletType !== "eoa" && (
                  <div data-testid="missing-wallet-warning" className="mb-4 px-3 py-2.5 rounded-lg text-[11px] font-medium flex items-start gap-2"
                    style={{ background: 'rgba(245, 158, 11, 0.08)', border: '1px solid rgba(245, 158, 11, 0.3)', color: 'var(--accent-yellow)' }}>
                    <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                    <div>
                      <div className="font-bold">Wallet Address Required</div>
                      <div className="text-[10px] mt-0.5 opacity-80">Your bot cannot trade without it. Go to polymarket.com, click your profile icon, and copy your wallet address (starts with 0x).</div>
                    </div>
                  </div>
                )}

                <Row label="Private Key" desc={hasCreds ? "Key saved. Enter new to replace." : "Your Polymarket private key"}>
                  <Input data-testid="poly-private-key-input" type="password" value={credKey}
                    onChange={(e) => { setCredKey(e.target.value); setCredChanged(true); }}
                    className="input-base" placeholder={hasCreds ? "Key saved" : "0x..."} />
                </Row>
                {credWalletType !== "eoa" && (
                  <Row label={<span className="flex items-center gap-1.5">Wallet Address {!credAddress && <span className="text-[9px] font-bold px-1.5 py-0.5 rounded" style={{ background: 'rgba(245, 158, 11, 0.15)', color: 'var(--accent-yellow)', border: '1px solid rgba(245, 158, 11, 0.3)' }}>REQUIRED</span>}</span>}
                    desc="Your Polymarket wallet address (Profile > Copy address)">
                    <Input data-testid="poly-funder-address-input" type="text" value={credAddress}
                      onChange={(e) => { setCredAddress(e.target.value); setCredChanged(true); }}
                      className="input-base" placeholder="0x..."
                      style={!credAddress ? { borderColor: 'rgba(245, 158, 11, 0.4)' } : {}} />
                  </Row>
                )}

                <div className="mt-4 flex flex-wrap gap-2">
                  <Button data-testid="save-credentials-btn" onClick={saveCredentials} disabled={savingCreds || !credChanged}
                    className="btn-primary h-8 px-4 text-xs disabled:opacity-40" style={{ color: '#0B0F1A' }}>
                    {savingCreds ? <Loader2 className="w-3 h-3 mr-1.5 animate-spin" /> : <Save className="w-3 h-3 mr-1.5" />}
                    {savingCreds ? "Saving..." : "Save Credentials"}
                  </Button>
                  <Button data-testid="test-connection-btn" onClick={testConnection} disabled={testing || !hasCreds}
                    className="btn-primary h-8 px-4 text-xs disabled:opacity-40" style={{ color: '#0B0F1A' }}>
                    {testing ? <Loader2 className="w-3 h-3 mr-1.5 animate-spin" /> : <Zap className="w-3 h-3 mr-1.5" />}
                    {testing ? "Testing..." : "Test Connection"}
                  </Button>
                  <Button data-testid="test-live-order-btn" onClick={testLiveOrder} disabled={testingLive || !hasCreds}
                    className="h-8 px-4 text-xs disabled:opacity-40 rounded-lg"
                    style={{ background: 'var(--accent-yellow)', color: '#0B0F1A' }}>
                    {testingLive ? <Loader2 className="w-3 h-3 mr-1.5 animate-spin" /> : <Zap className="w-3 h-3 mr-1.5" />}
                    {testingLive ? "Placing..." : "Test $2.50 Order"}
                  </Button>
                  {hasCreds && (
                    <Button data-testid="clear-key-btn" onClick={clearKey} variant="ghost"
                      className="h-8 px-3 text-xs text-[var(--accent-red)] hover:text-red-300 hover:bg-red-950/30">
                      <Trash2 className="w-3 h-3 mr-1" />Remove Key
                    </Button>
                  )}
                </div>

                {testResult && (
                  <div data-testid="test-connection-result" className={`mt-3 px-3 py-2 border text-xs font-mono ${testResult.success ? "bg-[rgba(0,229,168,0.06)] border-[rgba(0,229,168,0.2)]" : "bg-[rgba(239,68,68,0.06)] border-[rgba(239,68,68,0.2)]"}`}>
                    <div className={`font-semibold mb-1 ${testResult.success ? "text-[var(--accent-teal)]" : "text-[var(--accent-red)]"}`}>
                      {testResult.success ? "All checks passed" : "Connection check failed"}
                    </div>
                    {testResult.steps?.map((step, i) => <StepResult key={i} step={step} />)}
                    {testResult.message && <div className="mt-1 text-[var(--text-secondary)] text-[10px]">{testResult.message}</div>}
                  </div>
                )}

                {liveResult && (
                  <div data-testid="test-live-order-result" className={`mt-3 px-3 py-2 border text-xs font-mono ${liveResult.success ? "bg-[rgba(0,229,168,0.06)] border-[rgba(0,229,168,0.2)]" : "bg-[rgba(239,68,68,0.06)] border-[rgba(239,68,68,0.2)]"}`}>
                    {liveResult.success ? (
                      <div>
                        <div className="text-[var(--accent-teal)] font-semibold">{liveResult.message}</div>
                        {liveResult.order_id && <div className="text-[var(--text-secondary)] mt-1">Order ID: {liveResult.order_id}</div>}
                      </div>
                    ) : (
                      <div>
                        <div className="text-[var(--accent-red)] font-semibold">Order failed</div>
                        <div className="text-[var(--text-secondary)] mt-1">{liveResult.error}</div>
                      </div>
                    )}
                    {liveResult.steps_log?.length > 0 && (
                      <div className="mt-2 pt-2 border-t border-[var(--border)]/50">
                        {liveResult.steps_log.map((step, i) => <StepResult key={i} step={step} />)}
                      </div>
                    )}
                  </div>
                )}

              </div>
            </div>
            )}

            <ProxyListCard headers={headers} />

            {/* Reset PnL */}
            <div className="card-base border border-red-900/20">
              <div className="card-header flex items-center gap-2">
                <TrendingDown className="w-3.5 h-3.5 text-[var(--accent-red)]" />
                <span className="text-xs uppercase tracking-wider text-[var(--text-secondary)] font-semibold">Reset PnL</span>
              </div>
              <div className="card-content">
                <p className="text-[11px] text-[var(--text-secondary)] mb-3">Clear all profit/loss statistics and delete trade history.</p>
                <Button data-testid="reset-pnl-btn" onClick={resetPnl} disabled={resettingPnl}
                  className="h-8 px-4 text-xs bg-red-700 hover:bg-red-600 text-[var(--text-primary)] disabled:opacity-40">
                  {resettingPnl ? <Loader2 className="w-3 h-3 mr-1.5 animate-spin" /> : <TrendingDown className="w-3 h-3 mr-1.5" />}
                  {resettingPnl ? "Resetting..." : "Reset All PnL & Trades"}
                </Button>
              </div>
            </div>
          </div>

          {/* Right column: Ladder Editor */}
          <div className="lg:col-span-1">
            <div className="sticky top-16">
              <FadeBetsCard bets={s.fade_bets} bankroll={s.bankroll || 1000} onChange={(arr) => set("fade_bets", arr)} />
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Settings;
